/* eslint-disable react/no-unescaped-entities */
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import {
  ArrowLeft, Compass, Sparkle, Lightning, Target, Eye, Star,
  CaretRight, ArrowDown, Clock, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import KundaliChart from "../components/KundaliChart";
import ToolAstrologerCta from "../components/ToolAstrologerCta";

// =============================================================================
// PRECISION-ENGINEERING THEME
// KP is the most mathematical school of jyotish — we lean into that with a
// dark, cyan-and-gold palette inspired by mission-control consoles.
// =============================================================================
const TH = {
  bg:        "#0B0A1F",
  surface:   "#15132E",
  surfaceHi: "#1E1B40",
  border:    "#2D2A5E",
  borderHi:  "#4A4690",
  gold:      "#FFD16B",
  goldDim:   "#9C7A2B",
  cyan:      "#7CD9FF",
  cyanDim:   "#3F8FB3",
  lavender:  "#C792FF",
  rose:      "#FF94B3",
  text:      "#F4F1FF",
  textMute:  "#9999BB",
  textDim:   "#6E6A95",
};

// Per-planet brand colours — used across the page so a planet is instantly
// recognisable wherever it appears (cards, dasha bands, chain links).
const PLANET_COLORS = {
  Sun:     "#FFB347",
  Moon:    "#E0E7FF",
  Mars:    "#FF6B6B",
  Mercury: "#7CD9FF",
  Jupiter: "#FFD16B",
  Venus:   "#FF94B3",
  Saturn:  "#9999BB",
  Rahu:    "#9C7AFF",
  Ketu:    "#A8704F",
};

const PLANET_GLYPH = {
  Sun: "☉", Moon: "☽", Mars: "♂", Mercury: "☿", Jupiter: "♃",
  Venus: "♀", Saturn: "♄", Rahu: "☊", Ketu: "☋",
};

const ORDERED_PLANETS = ["Sun","Moon","Mars","Mercury","Jupiter","Venus","Saturn","Rahu","Ketu"];

// =============================================================================
// REUSABLE BITS
// =============================================================================

// A compact "pill" that shows a planet name in its own colour — used in every
// chain visualization on this page.
const PlanetPill = ({ name, size = "md", title = "", onHover, active = false }) => {
  const c = PLANET_COLORS[name] || TH.gold;
  const padding = size === "sm" ? "px-2 py-0.5 text-[10px]" : size === "lg" ? "px-3.5 py-1.5 text-sm" : "px-2.5 py-1 text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-mono font-bold tracking-wider ${padding} transition-all duration-200`}
      style={{
        backgroundColor: active ? c : `${c}1F`,
        color: active ? TH.bg : c,
        border: `1px solid ${c}66`,
        boxShadow: active ? `0 0 16px ${c}88` : "none",
      }}
      title={title}
      onMouseEnter={() => onHover?.(name)}
      onMouseLeave={() => onHover?.(null)}
    >
      <span style={{ fontSize: size === "sm" ? 10 : 13 }}>{PLANET_GLYPH[name]}</span>
      {name}
    </span>
  );
};

// The 4-tier KP DNA strand for a planet — Planet → Star → Sub → SubSub
// Each layer connected by a vertical thread. The strongest visual is the
// gold-glowing Sub-Lord row (the KP verdict-maker).
const KPChain = ({ planetName, info, highlight, setHighlight }) => {
  if (!info) return null;
  const isHL = (n) => highlight && (n === highlight);
  return (
    <div
      className="relative rounded-2xl p-4 transition-all duration-300"
      style={{
        backgroundColor: TH.surface,
        border: `1px solid ${TH.border}`,
        boxShadow: highlight && (info.star_lord === highlight || info.sub_lord === highlight || planetName === highlight)
          ? `0 0 28px ${PLANET_COLORS[highlight]}55`
          : "none",
      }}
      data-testid={`kp-chain-${planetName}`}
    >
      {/* Header: planet + KP house badge */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-lg"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${PLANET_COLORS[planetName]}55, ${PLANET_COLORS[planetName]}11)`,
              border: `1px solid ${PLANET_COLORS[planetName]}66`,
              color: PLANET_COLORS[planetName],
              boxShadow: `0 0 14px ${PLANET_COLORS[planetName]}33`,
            }}
          >
            {PLANET_GLYPH[planetName]}
          </div>
          <div>
            <div className="font-mono text-sm font-bold" style={{ color: PLANET_COLORS[planetName] }}>
              {planetName}{info.retrograde ? <span className="text-rose-400 text-[10px] ml-1">R</span> : ""}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: TH.textDim }}>
              {info.sign} · {info.nakshatra}
            </div>
          </div>
        </div>
        {info.kp_house && (
          <div
            className="px-2.5 py-1 rounded-md font-mono text-xs font-bold"
            style={{
              backgroundColor: `${TH.cyan}1A`,
              color: TH.cyan,
              border: `1px solid ${TH.cyan}33`,
            }}
            title="Bhav-Chalit (Placidus) house"
          >
            KP-H{info.kp_house}
          </div>
        )}
      </div>

      {/* DNA strand */}
      <div className="space-y-2">
        <ChainRow label="sign-lord"     planet={info.sign_lord}    isHL={isHL(info.sign_lord)}    setHL={setHighlight} colour={TH.textMute} />
        <ChainRow label="star-lord"     planet={info.star_lord}    isHL={isHL(info.star_lord)}    setHL={setHighlight} colour={TH.lavender} />
        <ChainRow label="sub-lord"      planet={info.sub_lord}     isHL={isHL(info.sub_lord)}     setHL={setHighlight} colour={TH.gold} accent />
        <ChainRow label="sub-sub-lord"  planet={info.sub_sub_lord} isHL={isHL(info.sub_sub_lord)} setHL={setHighlight} colour={TH.cyan} />
      </div>
    </div>
  );
};

const ChainRow = ({ label, planet, isHL, setHL, colour, accent }) => (
  <div className="flex items-center gap-2.5">
    <div
      className="font-mono text-[9px] uppercase tracking-[0.18em] w-24 shrink-0 text-right"
      style={{ color: TH.textDim }}
    >
      {label}
    </div>
    <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, transparent, ${colour}33, ${colour}88)` }} />
    <button
      type="button"
      onMouseEnter={() => setHL?.(planet)}
      onMouseLeave={() => setHL?.(null)}
      className="font-mono font-bold rounded-md px-2 py-0.5 transition-all"
      style={{
        color: accent ? TH.bg : (isHL ? TH.bg : colour),
        backgroundColor: accent ? colour : (isHL ? colour : `${colour}1A`),
        border: `1px solid ${colour}66`,
        fontSize: accent ? 13 : 11,
        boxShadow: accent ? `0 0 12px ${colour}55` : "none",
      }}
      title={`Hover to highlight all ${planet} placements`}
    >
      {PLANET_GLYPH[planet]} {planet}
    </button>
  </div>
);

// =============================================================================
// MAIN PAGE
// =============================================================================
export default function KPAstrologyPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // Lord-highlight — hovering any planet pill lights every occurrence of it
  // across the whole page (chains, cusps, dasha timeline). Helps the eye see
  // how one planet's energy fans out through 30+ KP positions.
  const [highlight, setHighlight] = useState(null);
  // Which mahadasha the user is currently viewing — defaults to the running
  // one but the toggle lets them dive into any past or future MD.
  const [focusMdIdx, setFocusMdIdx] = useState(null);
  // Which antardasha inside the focused MD is expanded for full KP insight.
  const [focusAdIdx, setFocusAdIdx] = useState(null);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tools/kp-analysis", form);
      setResult(data);
      // Auto-select the running mahadasha + antardasha so the user lands on
      // something useful instead of an empty timeline panel.
      const curIdx = (data.vimshottari?.mahadashas || []).findIndex((m) => m.is_current);
      setFocusMdIdx(curIdx >= 0 ? curIdx : 0);
      const curAdIdx = curIdx >= 0
        ? (data.vimshottari.mahadashas[curIdx].antardashas || []).findIndex((a) => a.is_current)
        : 0;
      setFocusAdIdx(curAdIdx >= 0 ? curAdIdx : 0);
      setTimeout(() => {
        document.querySelector("[data-testid='kp-result']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute KP analysis. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col" style={{ backgroundColor: TH.bg, color: TH.text }} data-testid="kp-page">
      {/* Cosmic backdrop — radial gradients give the precision-console feel. */}
      <div
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          background: `radial-gradient(ellipse at 20% 0%, ${TH.lavender}11 0%, transparent 50%),
                       radial-gradient(ellipse at 80% 100%, ${TH.cyan}0E 0%, transparent 50%),
                       ${TH.bg}`,
        }}
      />
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-1 text-sm mb-4"
          style={{ color: TH.textMute }}
          data-testid="kp-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        {/* Hero */}
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: TH.gold }}>
          <Target size={12} weight="duotone" /> Free · KP · Krishnamurti Paddhati · Precision Sub-Lord Theory
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight" style={{ color: TH.text }}>
          KP <span className="italic" style={{ color: TH.gold }}>Sub-Lord</span> Console
        </h1>
        <p className="text-sm sm:text-base mt-4 max-w-3xl font-light leading-relaxed" style={{ color: TH.textMute }}>
          Late <b style={{ color: TH.text }}>Prof. K.S. Krishnamurti</b>'s framework treats jyotish as
          precision engineering. Every longitude in the zodiac sits not just in a sign and nakshatra
          but a <b style={{ color: TH.gold }}>sub</b> — the final arbiter of every result. This console
          computes your full 4-tier signature (sign-lord → star-lord → sub-lord → sub-sub-lord) for
          every planet, every Placidus house cusp, your dasha layers, and the Ruling Planets of this
          exact moment.
        </p>

        {/* Form */}
        <form
          onSubmit={analyze}
          className="mt-8 rounded-2xl p-5 sm:p-7"
          style={{
            backgroundColor: TH.surface,
            border: `1px solid ${TH.border}`,
            boxShadow: `0 12px 60px -20px ${TH.lavender}33`,
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>Date of birth</span>
              <input
                type="date" required value={form.date} onChange={handle("date")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md font-mono text-sm"
                style={{
                  backgroundColor: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}`,
                  colorScheme: "dark",
                }}
                data-testid="kp-date"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>Time of birth</span>
              <input
                type="time" required value={form.time} onChange={handle("time")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md font-mono text-sm"
                style={{ backgroundColor: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}`, colorScheme: "dark" }}
                data-testid="kp-time"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="kp-place"
                  iconColor={TH.textDim}
                  inputClass="w-full px-3 py-2.5 pr-9 rounded-md font-mono text-sm outline-none"
                  /* Inline-styled to match the dark KP console palette so the
                     autocomplete dropdown doesn't clash with the page theme. */
                  dropdownClass="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md z-30 shadow-2xl"
                />
                <style>{`
                  [data-testid="kp-place"] {
                    background-color: ${TH.surfaceHi};
                    color: ${TH.text};
                    border: 1px solid ${TH.border};
                  }
                  [data-testid="kp-place"]:focus { border-color: ${TH.gold}; }
                  [data-testid="kp-place-suggestions"] {
                    background-color: ${TH.surfaceHi};
                    border: 1px solid ${TH.border};
                    color: ${TH.text};
                  }
                  [data-testid="kp-place-suggestions"] button:hover {
                    background-color: ${TH.surface};
                  }
                `}</style>
              </div>
            </label>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: TH.textDim }}>
              Computes Placidus cusps · Vimshottari · CSL · SSL · 9 MDs × 9 ADs
            </div>
            <button
              type="submit" disabled={busy}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-mono text-xs font-bold tracking-wider transition-all disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${TH.gold}, #FFAD3D)`,
                color: TH.bg,
                boxShadow: `0 0 28px ${TH.gold}55`,
              }}
              data-testid="kp-submit"
            >
              <Lightning size={14} weight="fill" />
              {busy ? "COMPUTING…" : "RUN KP ANALYSIS"}
            </button>
          </div>
        </form>

        {/* RESULTS */}
        {result && (
          <KPResults
            result={result}
            highlight={highlight}
            setHighlight={setHighlight}
            focusMdIdx={focusMdIdx}
            setFocusMdIdx={setFocusMdIdx}
            focusAdIdx={focusAdIdx}
            setFocusAdIdx={setFocusAdIdx}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

// =============================================================================
// RESULTS PANEL — broken into sub-components so it stays readable.
// =============================================================================
const KPResults = ({ result, highlight, setHighlight, focusMdIdx, setFocusMdIdx, focusAdIdx, setFocusAdIdx }) => {
  const { lagna, moon, kp, vimshottari } = result;
  const rp = kp.ruling_planets || {};
  const mds = vimshottari.mahadashas || [];
  const focusMd = mds[focusMdIdx] || mds[0];
  const focusAd = focusMd?.antardashas?.[focusAdIdx] || focusMd?.antardashas?.[0];

  return (
    <div className="mt-10 space-y-10" data-testid="kp-result">
      {/* Identity bar */}
      <IdentityBar lagna={lagna} moon={moon} kp={kp} setHighlight={setHighlight} />

      {/* LAGNA + KP charts (side by side on desktop, stacked on mobile). */}
      <Section
        title="Charts — Lagna · KP (Bhav-Chalit)"
        sub="The Lagna (rashi) chart uses whole-sign houses. The KP/Bhav-Chalit chart drops every planet into the Placidus house its longitude actually falls in — and that's the one KP reads for cuspal results."
      >
        <ChartsPair lagna={lagna} moon={moon} kp={kp} />
      </Section>

      {/* Ruling Planets HUD */}
      <RulingPlanetsHUD rp={rp} setHighlight={setHighlight} />

      {/* 9 PLANETS GRID */}
      <Section title="Planet Sub-Lord Signatures" sub="All 9 grahas decoded in KP's 4-tier sub-lord chain. Hover any planet pill to light up every placement that shares it.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ORDERED_PLANETS.map((p) => (
            <KPChain
              key={p}
              planetName={p}
              info={kp.planets_kp?.[p]}
              highlight={highlight}
              setHighlight={setHighlight}
            />
          ))}
        </div>
      </Section>

      {/* PLANET HOUSE ACTIVATIONS — the 3-step KP chain. */}
      <Section
        title="Planet House Activations (3-Step KP Chain)"
        sub="What each planet PROMISES to deliver during its dasha. KP runs three layers — Planet → Star-Lord (strongest signification) → Sub-Lord (final outcome direction). All houses touched by any layer light up."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ORDERED_PLANETS.map((p) => (
            <ActivationCard
              key={p}
              planetName={p}
              act={kp.activations?.[p]}
              setHighlight={setHighlight}
              highlight={highlight}
            />
          ))}
        </div>
      </Section>

      {/* LIFE DOMAIN PROMISERS — classical KP house-combos × all 9 planets. */}
      <Section
        title="Life Domain Promisers"
        sub="Each of the 6 life-aspects has a classical KP house combo. We score every planet's activation set against the positive AND dusthana sides — so you see at a glance which planets' dashas WILL deliver each matter, and which to brace for."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kp.life_domains && Object.entries(kp.life_domains).map(([key, dom]) => (
            <DomainCard key={key} domainKey={key} dom={dom} setHighlight={setHighlight} highlight={highlight} />
          ))}
        </div>
      </Section>

      {/* 12 CUSPAL SUB-LORDS */}
      <Section title="Cuspal Sub-Lords (CSL)" sub="The decisive marker for every house's matter. 7H-CSL = marriage verdict · 10H-CSL = career verdict · 11H-CSL = fulfilment of desires.">
        <CuspsTable cusps={kp.cusps_kp || []} significators={kp.significators || {}} highlight={highlight} setHighlight={setHighlight} />
      </Section>

      {/* DASHA TIMELINE */}
      <Section
        title="Vimshottari × KP — Mahadasha Console"
        sub={`Click any of the 9 mahadashas to dive in. The currently-running MD is ${vimshottari.current_md_lord} / ${vimshottari.current_ad_lord}.`}
      >
        <DashaTimeline
          mds={mds}
          focusMdIdx={focusMdIdx}
          setFocusMdIdx={(i) => { setFocusMdIdx(i); setFocusAdIdx(0); }}
        />
        <AntardashaPanel
          md={focusMd}
          focusAdIdx={focusAdIdx}
          setFocusAdIdx={setFocusAdIdx}
          ad={focusAd}
        />
      </Section>

      {/* KP astrologer CTA — only at the bottom, after the user has seen all
          the analysis. The ToolAstrologerCta component matches by
          `technique` string (case-insensitive) to fetch the right astrologer. */}
      <ToolAstrologerCta
        requiredTechnique="KP Sub-Lord Theory"
        pitch="Want a senior KP astrologer to read your CSL chain live?"
        ctaLabel="Read with Pandit Krishnamurti Iyer →"
      />
    </div>
  );
};

// -----------------------------------------------------------------------------
// CHARTS PAIR — Lagna (rashi/whole-sign) on the left, KP (bhav-chalit /
// Placidus) on the right. Both render in the same North-Indian style so the
// reader can visually compare a planet's whole-sign house vs its actual
// KP-house — the cornerstone insight of Krishnamurti Paddhati.
// -----------------------------------------------------------------------------
const ChartsPair = ({ lagna, moon, kp }) => {
  // Synthesize the kundali-shaped object that <KundaliChart> needs from the
  // KP-analysis payload (which already carries planet rashi via `sign`).
  const synthKundali = useMemo(() => {
    if (!kp?.planets_kp) return null;
    const planets = {};
    Object.entries(kp.planets_kp).forEach(([name, info]) => {
      planets[name] = {
        rashi: info.sign,
        retrograde: !!info.retrograde,
      };
    });
    return {
      lagna: { rashi: lagna.rashi },
      planets,
    };
  }, [kp, lagna]);

  // KP chart: planet → kp_house map (from the cuspal-Placidus computation)
  const houseOverrides = useMemo(() => {
    if (!kp?.planets_kp) return null;
    const m = {};
    Object.entries(kp.planets_kp).forEach(([name, info]) => {
      if (info.kp_house) m[name] = info.kp_house;
    });
    return m;
  }, [kp]);

  // KP chart: house → sign on cusp (Placidus puts a different sign on some cusps)
  const houseSigns = useMemo(() => {
    if (!kp?.cusps_kp) return null;
    const m = {};
    kp.cusps_kp.forEach((c) => { m[c.house] = c.sign; });
    return m;
  }, [kp]);

  if (!synthKundali || !houseOverrides || !houseSigns) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <ChartFrame
        title="Lagna Chart"
        sub={`Whole-sign · ${lagna.rashi} ascendant · ruler ${lagna.rashi_lord}`}
      >
        <KundaliChart kundali={synthKundali} size={300} />
      </ChartFrame>

      <ChartFrame
        title="KP / Bhav-Chalit Chart"
        sub={`Placidus cusps · what KP reads for verdicts · Moon nakshatra: ${moon.nakshatra}${moon.pada ? ` (pada ${moon.pada})` : ""}`}
        accent
      >
        <KundaliChart
          kundali={synthKundali}
          size={300}
          houseOverrides={houseOverrides}
          houseSigns={houseSigns}
        />
      </ChartFrame>
    </div>
  );
};

const ChartFrame = ({ title, sub, accent, children }) => (
  <div
    className="rounded-2xl p-4 sm:p-5 flex flex-col items-center"
    style={{
      backgroundColor: TH.surface,
      border: `1px solid ${accent ? TH.gold + "55" : TH.border}`,
      boxShadow: accent ? `0 0 28px ${TH.gold}1A` : "none",
    }}
  >
    <div className="w-full text-center mb-3">
      <div
        className="font-mono text-[10px] uppercase tracking-[0.25em] mb-1"
        style={{ color: accent ? TH.gold : TH.cyan }}
      >
        {title}
      </div>
      <div className="text-sm font-light" style={{ color: TH.textMute }}>{sub}</div>
    </div>
    <div className="rounded-md overflow-hidden" style={{ boxShadow: `0 8px 24px -10px ${TH.bg}` }}>
      {children}
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// IDENTITY BAR — Lagna + Moon KP signatures side by side at the top.
// -----------------------------------------------------------------------------
const IdentityBar = ({ lagna, moon, kp, setHighlight }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <IdentityCard
      icon={<Compass size={18} weight="duotone" />}
      title="Lagna"
      subtitle={`${lagna.rashi} · ${lagna.rashi_lord}`}
      degLabel={lagna.longitude ? `${lagna.longitude.toFixed(2)}°` : ""}
      chain={kp.lagna_kp}
      setHighlight={setHighlight}
    />
    <IdentityCard
      icon={<Star size={18} weight="duotone" />}
      title="Moon"
      subtitle={`${moon.rashi} · ${moon.nakshatra}${moon.pada ? ` (pada ${moon.pada})` : ""}`}
      degLabel={moon.longitude ? `${moon.longitude.toFixed(2)}°` : ""}
      chain={kp.moon_kp}
      setHighlight={setHighlight}
    />
  </div>
);

const IdentityCard = ({ icon, title, subtitle, degLabel, chain, setHighlight }) => (
  <div className="rounded-2xl p-4 sm:p-5" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${TH.cyan}1A`, color: TH.cyan }}>
          {icon}
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>{title}</div>
          <div className="text-base font-light" style={{ color: TH.text }}>{subtitle}</div>
        </div>
      </div>
      {degLabel && (
        <div className="font-mono text-xs" style={{ color: TH.gold }}>{degLabel}</div>
      )}
    </div>
    {chain && (
      <div className="flex flex-wrap gap-1.5">
        <PlanetPill name={chain.sign_lord} size="sm" title="sign-lord" onHover={setHighlight} />
        <CaretRight size={12} color={TH.textDim} className="self-center" />
        <PlanetPill name={chain.star_lord} size="sm" title="star-lord" onHover={setHighlight} />
        <CaretRight size={12} color={TH.gold} className="self-center" />
        <PlanetPill name={chain.sub_lord} size="md" title="SUB-LORD (KP verdict-maker)" onHover={setHighlight} />
        <CaretRight size={12} color={TH.textDim} className="self-center" />
        <PlanetPill name={chain.sub_sub_lord} size="sm" title="sub-sub-lord" onHover={setHighlight} />
      </div>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// RULING PLANETS HUD — sticky display of the 5-planet RP set for today.
// -----------------------------------------------------------------------------
const RulingPlanetsHUD = ({ rp, setHighlight }) => {
  const items = useMemo(() => {
    const list = [];
    if (rp.day_lord) list.push({ label: "Day-Lord", planet: rp.day_lord, sub: rp.weekday });
    if (rp.moon_star_lord) list.push({ label: "Moon Star", planet: rp.moon_star_lord });
    if (rp.moon_sub_lord) list.push({ label: "Moon Sub", planet: rp.moon_sub_lord });
    if (rp.lagna_star_lord) list.push({ label: "Lagna Star", planet: rp.lagna_star_lord });
    if (rp.lagna_sub_lord) list.push({ label: "Lagna Sub", planet: rp.lagna_sub_lord });
    return list;
  }, [rp]);

  // Count duplicates — a planet appearing twice in the RP set is a strong
  // confirmation marker in KP horary.
  const counts = items.reduce((acc, it) => ({ ...acc, [it.planet]: (acc[it.planet] || 0) + 1 }), {});

  return (
    <div
      className="rounded-2xl p-4 sm:p-5 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${TH.surface}, ${TH.surfaceHi})`,
        border: `1px solid ${TH.border}`,
      }}
      data-testid="kp-rp-hud"
    >
      {/* Pulse glow */}
      <div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30 pointer-events-none animate-pulse"
        style={{ background: `radial-gradient(circle, ${TH.cyan}, transparent 70%)` }}
      />
      <div className="flex items-center justify-between mb-3 relative">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: TH.cyan }}>Ruling Planets · This Moment</div>
          <div className="text-sm font-light mt-0.5" style={{ color: TH.textMute }}>
            The 5 planets in command right now — used in KP horary to confirm any verdict.
          </div>
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px]" style={{ color: TH.textDim }}>
          <Clock size={12} /> {new Date().toLocaleString()}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 relative">
        {items.map((it) => (
          <div
            key={it.label}
            className="flex flex-col gap-1 rounded-lg px-2.5 py-2"
            style={{
              backgroundColor: TH.surfaceHi,
              border: `1px solid ${counts[it.planet] > 1 ? PLANET_COLORS[it.planet] : TH.border}`,
              boxShadow: counts[it.planet] > 1 ? `0 0 16px ${PLANET_COLORS[it.planet]}44` : "none",
            }}
          >
            <div className="font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: TH.textDim }}>
              {it.label} {it.sub ? `· ${it.sub}` : ""}
            </div>
            <PlanetPill name={it.planet} onHover={setHighlight} />
          </div>
        ))}
      </div>
      <div className="mt-3 font-mono text-[10px]" style={{ color: TH.textDim }}>
        Tip: a planet appearing TWICE in this set glows brighter — it's a confirmation-grade RP for any KP verdict you read today.
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// CUSPS TABLE — 12 rows, each with CSL highlighted.
// -----------------------------------------------------------------------------
const CuspsTable = ({ cusps, significators, highlight, setHighlight }) => {
  const KEY_HOUSE_NAMES = {
    1: "Self · Body · Brand", 2: "Wealth · Family · Voice", 3: "Courage · Siblings · Effort",
    4: "Mother · Home · Real Estate", 5: "Romance · Children · Intelligence", 6: "Service · Debt · Health",
    7: "Spouse · Partners · Contracts", 8: "Sudden · Occult · Longevity", 9: "Father · Guru · Fortune",
    10: "Career · Status · Authority", 11: "Income · Gains · Desires", 12: "Expenses · Foreign · Moksha",
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}>
      <div className="grid grid-cols-12 gap-2 px-3 sm:px-4 py-2.5 text-[9px] font-mono uppercase tracking-[0.15em]" style={{ color: TH.textDim, backgroundColor: TH.surfaceHi }}>
        <div className="col-span-1">H</div>
        <div className="col-span-3 sm:col-span-2">Cusp</div>
        <div className="col-span-3 sm:col-span-3">Sign · Nakshatra</div>
        <div className="hidden sm:block col-span-2">Star-Lord</div>
        <div className="col-span-3 sm:col-span-2">CSL</div>
        <div className="hidden lg:block col-span-2">SSL · Theme</div>
        <div className="col-span-2 sm:col-span-0 hidden">.</div>
      </div>
      {cusps.map((c) => {
        const themeShort = (KEY_HOUSE_NAMES[c.house] || "").split("·")[0].trim();
        const csl = c.sub_lord;
        const cslGlow = highlight === csl;
        return (
          <div
            key={c.house}
            className="grid grid-cols-12 gap-2 px-3 sm:px-4 py-2.5 items-center text-xs transition-colors"
            style={{
              borderTop: `1px solid ${TH.border}`,
              backgroundColor: cslGlow ? `${PLANET_COLORS[csl]}11` : "transparent",
            }}
          >
            <div className="col-span-1 font-mono font-bold" style={{ color: TH.cyan }}>{c.house}</div>
            <div className="col-span-3 sm:col-span-2 font-mono" style={{ color: TH.textMute }}>{c.longitude?.toFixed(2)}°</div>
            <div className="col-span-3 sm:col-span-3">
              <div className="font-light" style={{ color: TH.text }}>{c.sign}</div>
              <div className="font-mono text-[10px]" style={{ color: TH.textDim }}>{c.nakshatra}</div>
            </div>
            <div className="hidden sm:block col-span-2"><PlanetPill name={c.star_lord} size="sm" onHover={setHighlight} active={highlight === c.star_lord} /></div>
            <div className="col-span-3 sm:col-span-2"><PlanetPill name={csl} size="md" title="Cuspal Sub-Lord — the decisive marker" onHover={setHighlight} active={cslGlow} /></div>
            <div className="hidden lg:block col-span-2 font-mono text-[10px]" style={{ color: TH.textDim }}>
              <span style={{ color: PLANET_COLORS[c.sub_sub_lord] }}>{c.sub_sub_lord}</span> · {themeShort}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// DASHA TIMELINE — horizontal scroll of 9 MD bands, click to focus.
// -----------------------------------------------------------------------------
const DashaTimeline = ({ mds, focusMdIdx, setFocusMdIdx }) => {
  // Auto-scroll the active card into view on mount.
  useEffect(() => {
    const el = document.querySelector(`[data-md-idx="${focusMdIdx}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [focusMdIdx]);

  return (
    <div className="-mx-3 sm:-mx-0 px-3 sm:px-0 overflow-x-auto pb-2" data-testid="kp-dasha-timeline">
      <div className="flex gap-3" style={{ minWidth: "max-content" }}>
        {mds.map((md, i) => {
          const c = PLANET_COLORS[md.lord];
          const active = focusMdIdx === i;
          const current = md.is_current;
          return (
            <button
              key={`${md.lord}-${md.from}`}
              type="button"
              onClick={() => setFocusMdIdx(i)}
              data-md-idx={i}
              data-testid={`kp-md-${i}`}
              className="flex flex-col items-stretch text-left rounded-xl overflow-hidden transition-all shrink-0"
              style={{
                width: 180,
                backgroundColor: TH.surface,
                border: `1px solid ${active ? c : TH.border}`,
                boxShadow: active ? `0 0 24px ${c}55` : "none",
                opacity: md.is_past ? 0.7 : 1,
              }}
            >
              <div className="h-1.5" style={{ background: `linear-gradient(to right, ${c}, ${c}77)` }} />
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>
                    {md.is_past ? "PAST" : current ? "RUNNING NOW" : "UPCOMING"}
                  </div>
                  <div className="font-mono text-[10px]" style={{ color: TH.textMute }}>{md.years}y</div>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-2xl" style={{ color: c }}>{PLANET_GLYPH[md.lord]}</span>
                  <span className="font-mono text-base font-bold" style={{ color: c }}>{md.lord}</span>
                </div>
                <div className="mt-1 font-mono text-[10px]" style={{ color: TH.textDim }}>
                  {(md.from || "").slice(0, 7)} → {(md.to || "").slice(0, 7)}
                </div>
                {current && (
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px] font-bold animate-pulse"
                       style={{ backgroundColor: `${TH.gold}22`, color: TH.gold, border: `1px solid ${TH.gold}66` }}>
                    <Sparkle size={9} weight="fill" /> ACTIVE
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// ANTARDASHA PANEL — the 9 ADs of the focused MD + the full insight for the
// selected AD. This is the crown jewel of the page.
// -----------------------------------------------------------------------------
const AntardashaPanel = ({ md, focusAdIdx, setFocusAdIdx, ad }) => {
  if (!md) return null;
  const cMd = PLANET_COLORS[md.lord];
  return (
    <div className="mt-5 rounded-2xl overflow-hidden" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}>
      <div className="px-4 sm:px-5 py-3" style={{ background: `linear-gradient(90deg, ${cMd}22, transparent)`, borderBottom: `1px solid ${TH.border}` }}>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-2xl" style={{ color: cMd }}>{PLANET_GLYPH[md.lord]}</span>
          <span className="font-mono text-lg font-bold" style={{ color: cMd }}>{md.lord} Mahadasha</span>
          <span className="font-mono text-[11px]" style={{ color: TH.textDim }}>
            {md.from} → {md.to} · {md.years}y
          </span>
        </div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-widest" style={{ color: TH.textDim }}>
          9 antardashas — click any to read its KP insight
        </div>
      </div>

      {/* AD strip — proportional widths */}
      <div className="px-4 sm:px-5 pt-4">
        <div className="flex h-7 rounded-lg overflow-hidden" style={{ border: `1px solid ${TH.border}` }}>
          {(md.antardashas || []).map((a, i) => {
            const c = PLANET_COLORS[a.lord];
            const active = focusAdIdx === i;
            const pct = (a.years / (md.years || 1)) * 100;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setFocusAdIdx(i)}
                className="relative flex items-center justify-center font-mono text-[10px] font-bold transition-all hover:brightness-125"
                style={{
                  width: `${pct}%`,
                  backgroundColor: active ? c : `${c}66`,
                  color: active ? TH.bg : TH.bg,
                  borderRight: `1px solid ${TH.bg}`,
                  boxShadow: active ? `inset 0 0 0 2px ${TH.text}` : "none",
                }}
                title={`${a.lord} · ${a.from} → ${a.to}`}
                data-testid={`kp-ad-${i}`}
              >
                {pct > 6 ? PLANET_GLYPH[a.lord] : ""}
                {a.is_current && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: TH.gold, boxShadow: `0 0 10px ${TH.gold}` }} />
                )}
              </button>
            );
          })}
        </div>
        {/* Labels strip — names + dates on a separate row so the bars stay clean */}
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {(md.antardashas || []).map((a, i) => {
            const c = PLANET_COLORS[a.lord];
            const active = focusAdIdx === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setFocusAdIdx(i)}
                className="text-left rounded-md px-2 py-1.5 transition-all"
                style={{
                  backgroundColor: active ? `${c}1A` : "transparent",
                  border: `1px solid ${active ? c : TH.border}`,
                }}
              >
                <div className="font-mono text-[10px] font-bold flex items-center gap-1" style={{ color: c }}>
                  {PLANET_GLYPH[a.lord]} {a.lord}
                  {a.is_current && <span className="text-[8px] px-1 rounded" style={{ backgroundColor: TH.gold, color: TH.bg }}>NOW</span>}
                </div>
                <div className="font-mono text-[9px]" style={{ color: TH.textDim }}>
                  {(a.from || "").slice(0, 7)}<br />→ {(a.to || "").slice(0, 7)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected AD insight */}
      {ad && (
        <div className="px-4 sm:px-5 py-5 mt-2" style={{ borderTop: `1px solid ${TH.border}` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl" style={{ color: PLANET_COLORS[ad.lord] }}>{PLANET_GLYPH[ad.lord]}</span>
            <span className="font-mono text-base font-bold" style={{ color: PLANET_COLORS[ad.lord] }}>
              {md.lord} → {ad.lord}
            </span>
            <span className="font-mono text-[11px]" style={{ color: TH.textDim }}>
              {ad.from} → {ad.to} · {ad.years}y
            </span>
            {ad.is_current && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: TH.gold, color: TH.bg }}>
                RUNNING NOW
              </span>
            )}
          </div>
          <div
            className="text-sm leading-relaxed font-light"
            style={{ color: TH.text }}
            data-testid="kp-ad-insight"
            dangerouslySetInnerHTML={{ __html: formatBold(ad.kp_insight || "") }}
          />
        </div>
      )}
    </div>
  );
};

// Convert **bold** markdown into <b style="color:gold"> for emphasis since
// the deterministic insight engine uses ** markers throughout.
const formatBold = (s) =>
  String(s || "").replace(/\*\*(.+?)\*\*/g, `<b style="color:${TH.gold}">$1</b>`);

// -----------------------------------------------------------------------------
// LIFE DOMAIN CARD — one per aspect (wealth · career · fame · travels · health
// · relationships). Shows the KP house combo, then the 9 planets bucketed by
// verdict (STRONG · MODERATE · NEUTRAL · WARNING). Click any planet to expand
// its insight for THIS domain.
// -----------------------------------------------------------------------------
const VERDICT_STYLE = {
  strong:   { color: "#5EE0A8", bg: "rgba(94,224,168,0.10)",  border: "rgba(94,224,168,0.45)", label: "STRONG" },
  moderate: { color: TH.cyan,   bg: `${TH.cyan}10`,           border: `${TH.cyan}55`,         label: "MODERATE" },
  neutral:  { color: TH.textMute, bg: `${TH.textMute}10`,      border: `${TH.textMute}33`,    label: "NEUTRAL" },
  warning:  { color: "#FF7A8E", bg: "rgba(255,122,142,0.10)", border: "rgba(255,122,142,0.45)", label: "WARNING" },
};
const DOMAIN_ICON = {
  wealth:        "₹", career: "▲", fame: "★",
  travels:       "✈", health: "♡", relationships: "❤︎",
};

const DomainCard = ({ domainKey, dom, setHighlight, highlight }) => {
  const [selectedPlanet, setSelectedPlanet] = useState(null);

  // Bucket planets by verdict, sorted by score within each bucket (desc).
  const buckets = useMemo(() => {
    const byVerdict = { strong: [], moderate: [], neutral: [], warning: [] };
    Object.entries(dom.planets || {}).forEach(([p, row]) => {
      byVerdict[row.verdict || "neutral"].push({ planet: p, ...row });
    });
    Object.keys(byVerdict).forEach((k) => {
      byVerdict[k].sort((a, b) => (b.score || 0) - (a.score || 0));
    });
    return byVerdict;
  }, [dom]);

  const sel = selectedPlanet && dom.planets?.[selectedPlanet];

  return (
    <div
      className="rounded-2xl p-4 sm:p-5"
      style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}
      data-testid={`kp-domain-${domainKey}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xl" style={{ color: TH.gold }}>{DOMAIN_ICON[domainKey]}</span>
            <span className="font-mono text-sm font-bold uppercase tracking-wider" style={{ color: TH.text }}>{dom.label}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5 items-center text-[10px] font-mono">
            <span style={{ color: TH.textDim }}>BENEFIC:</span>
            {(dom.positives || []).map((h) => (
              <span key={h} className="px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: `${TH.cyan}1A`, color: TH.cyan, border: `1px solid ${TH.cyan}33` }}>H{h}</span>
            ))}
            <span className="ml-2" style={{ color: TH.textDim }}>DUSTHANA:</span>
            {(dom.negatives || []).map((h) => (
              <span key={h} className="px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: `rgba(255,122,142,0.10)`, color: "#FF7A8E", border: `1px solid rgba(255,122,142,0.33)` }}>H{h}</span>
            ))}
          </div>
        </div>
      </div>

      {/* House-combo note */}
      <div className="font-mono text-[10px] leading-snug mb-3" style={{ color: TH.textDim }}>
        {dom.note}
      </div>

      {/* Verdict-bucketed planet rows */}
      <div className="space-y-2">
        {["strong", "moderate", "neutral", "warning"].map((v) => {
          if (!buckets[v].length) return null;
          const s = VERDICT_STYLE[v];
          return (
            <div key={v} className="flex items-start gap-2">
              <div
                className="font-mono text-[9px] font-bold tracking-[0.18em] shrink-0 mt-1.5 px-2 py-0.5 rounded"
                style={{ width: 76, color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}` }}
              >
                {s.label}
              </div>
              <div className="flex-1 flex flex-wrap gap-1.5">
                {buckets[v].map((row) => {
                  const isSelected = selectedPlanet === row.planet;
                  const c = PLANET_COLORS[row.planet];
                  return (
                    <button
                      key={row.planet}
                      type="button"
                      onClick={() => setSelectedPlanet(isSelected ? null : row.planet)}
                      onMouseEnter={() => setHighlight?.(row.planet)}
                      onMouseLeave={() => setHighlight?.(null)}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-[11px] font-bold transition-all"
                      style={{
                        backgroundColor: isSelected ? c : `${c}1A`,
                        color: isSelected ? TH.bg : c,
                        border: `1px solid ${c}66`,
                        boxShadow: isSelected ? `0 0 14px ${c}66` : highlight === row.planet ? `0 0 8px ${c}33` : "none",
                      }}
                      data-testid={`kp-domain-${domainKey}-${row.planet}`}
                    >
                      {PLANET_GLYPH[row.planet]} {row.planet}
                      <span
                        className="px-1 rounded font-bold"
                        style={{
                          fontSize: 9,
                          backgroundColor: isSelected ? `${TH.bg}33` : `${c}1A`,
                          color: isSelected ? TH.bg : c,
                        }}
                      >
                        {row.score > 0 ? `+${row.score}` : row.score}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expanded insight for selected planet */}
      {sel && (
        <div
          className="mt-3 pt-3 text-[12px] leading-relaxed font-light"
          style={{ borderTop: `1px dashed ${TH.border}`, color: TH.text }}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-base" style={{ color: PLANET_COLORS[selectedPlanet] }}>{PLANET_GLYPH[selectedPlanet]}</span>
            <span className="font-mono text-[11px] font-bold" style={{ color: PLANET_COLORS[selectedPlanet] }}>{selectedPlanet}</span>
            <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: VERDICT_STYLE[sel.verdict].color }}>· {VERDICT_STYLE[sel.verdict].label}</span>
          </div>
          <div dangerouslySetInnerHTML={{ __html: formatBold(sel.insight || "") }} />
        </div>
      )}

      {/* Default helper when nothing selected */}
      {!sel && (
        <div className="mt-3 pt-3 font-mono text-[10px]" style={{ borderTop: `1px dashed ${TH.border}`, color: TH.textDim }}>
          Click any planet pill to read its specific {dom.label.toLowerCase()} reading.
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// PLANET ACTIVATION CARD — the 3-step KP chain (planet · star-lord · sub-lord),
// showing for each layer the house occupied + houses owned, and culminating in
// a triple of house-chip rows: ALL ACTIVATED · PRIMARY (via star-lord) · FINAL
// DIRECTION (via sub-lord). Hover-cross-highlight wired into the page state.
// -----------------------------------------------------------------------------
const ActivationCard = ({ planetName, act, highlight, setHighlight }) => {
  if (!act) return null;
  const c = PLANET_COLORS[planetName];
  const starC = PLANET_COLORS[act.star_lord];
  const subC = PLANET_COLORS[act.sub_lord];
  return (
    <div
      className="rounded-2xl p-4 sm:p-5 transition-all"
      style={{
        backgroundColor: TH.surface,
        border: `1px solid ${TH.border}`,
        boxShadow: highlight === planetName ? `0 0 28px ${c}55` : "none",
      }}
      data-testid={`kp-activation-${planetName}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${c}66, ${c}11)`,
            border: `1px solid ${c}66`,
            color: c,
            boxShadow: `0 0 16px ${c}33`,
          }}
        >
          {PLANET_GLYPH[planetName]}
        </div>
        <div className="flex-1">
          <div className="font-mono text-base font-bold" style={{ color: c }}>{planetName}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: TH.textDim }}>
            What it activates during its dasha
          </div>
        </div>
      </div>

      {/* Three-row chain */}
      <div className="space-y-2.5">
        <ActivationRow
          label="The Planet"
          planet={planetName}
          house={act.planet_house}
          owned={act.planet_houses_owned}
          colour={c}
          setHighlight={setHighlight}
          highlight={highlight}
        />
        <ActivationRow
          label="Star-Lord (strongest)"
          planet={act.star_lord}
          house={act.star_lord_house}
          owned={act.star_lord_houses_owned}
          colour={starC}
          setHighlight={setHighlight}
          highlight={highlight}
          emphasis="primary"
        />
        <ActivationRow
          label="Sub-Lord (final direction)"
          planet={act.sub_lord}
          house={act.sub_lord_house}
          owned={act.sub_lord_houses_owned}
          colour={subC}
          setHighlight={setHighlight}
          highlight={highlight}
          emphasis="final"
        />
      </div>

      {/* Activation summary chips */}
      <div className="mt-4 pt-3 space-y-2" style={{ borderTop: `1px dashed ${TH.border}` }}>
        <ActivationChipsRow
          label="ACTIVATES"
          houses={act.activated_houses}
          colour={TH.cyan}
        />
        <ActivationChipsRow
          label="PRIMARY"
          houses={act.primary_houses}
          colour={TH.lavender}
          hint="strongest signification (via star-lord)"
        />
        <ActivationChipsRow
          label="FINAL"
          houses={act.final_direction_houses}
          colour={TH.gold}
          hint="verdict / outcome direction (via sub-lord)"
          glow
        />
      </div>

      {/* Insight paragraph */}
      {act.insight && (
        <div
          className="mt-3 text-[12px] leading-relaxed font-light"
          style={{ color: TH.text }}
          dangerouslySetInnerHTML={{ __html: formatBold(act.insight) }}
        />
      )}
    </div>
  );
};

const ActivationRow = ({ label, planet, house, owned, colour, setHighlight, highlight, emphasis }) => {
  const isHL = highlight === planet;
  const accent = emphasis === "final";
  return (
    <div
      className="flex items-start gap-3 rounded-lg p-2"
      style={{
        backgroundColor: accent ? `${colour}0E` : "transparent",
        border: accent ? `1px solid ${colour}33` : `1px solid transparent`,
      }}
    >
      <div
        className="font-mono text-[9px] uppercase tracking-[0.18em] shrink-0 mt-1"
        style={{ color: emphasis === "primary" ? TH.lavender : emphasis === "final" ? TH.gold : TH.textDim, width: 120 }}
      >
        {label}
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-1.5">
        <PlanetPill name={planet} size="md" onHover={setHighlight} active={isHL} />
        {house ? (
          <span
            className="px-2 py-0.5 rounded-md font-mono text-[10px] font-bold"
            style={{ backgroundColor: `${TH.cyan}1A`, color: TH.cyan, border: `1px solid ${TH.cyan}33` }}
            title="House occupied"
          >
            in H{house}
          </span>
        ) : null}
        {owned && owned.length > 0 && (
          <span className="font-mono text-[10px]" style={{ color: TH.textDim }}>
            · rules {owned.map((h) => <span key={h} className="px-1 mx-0.5 rounded" style={{ backgroundColor: `${colour}22`, color: colour }}>H{h}</span>)}
          </span>
        )}
      </div>
    </div>
  );
};

const ActivationChipsRow = ({ label, houses, colour, hint, glow }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span
      className="font-mono text-[9px] uppercase tracking-[0.2em] mr-1 shrink-0"
      style={{ color: colour }}
      title={hint}
    >
      {label}
    </span>
    {(houses || []).length === 0 ? (
      <span className="font-mono text-[10px]" style={{ color: TH.textDim }}>—</span>
    ) : (
      houses.map((h) => (
        <span
          key={h}
          className="px-2 py-0.5 rounded-md font-mono text-[11px] font-bold"
          style={{
            backgroundColor: glow ? colour : `${colour}1A`,
            color: glow ? TH.bg : colour,
            border: `1px solid ${colour}66`,
            boxShadow: glow ? `0 0 10px ${colour}55` : "none",
          }}
        >
          H{h}
        </span>
      ))
    )}
  </div>
);

// -----------------------------------------------------------------------------
// SECTION wrapper — consistent heading style for the long page.
// -----------------------------------------------------------------------------
const Section = ({ title, sub, children }) => (
  <section>
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: TH.gold }}>// {title}</div>
      <div className="mt-1 text-sm sm:text-base font-light" style={{ color: TH.textMute }}>{sub}</div>
    </div>
    {children}
  </section>
);
